import PropTypes from 'prop-types';
import React from 'react';

import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';

import Timeline1 from '../../../common/components/svgs/rewards-timeline/offer-modal-step-1-calendar.svg';
import Timeline2 from '../../../common/components/svgs/rewards-timeline/offer-modal-step-2-clinic.svg';
import Timeline3 from '../../../common/components/svgs/rewards-timeline/offer-modal-step-3-receipt.svg';
import Timeline4 from '../../../common/components/svgs/rewards-timeline/offer-modal-step-4-giftcard.svg';
import { OFFER_TERMS_URL } from '../../../common/constants/search-constants';

import {
  Checkmark,
  ContentCard,
  EligibilityCard,
  EligibilityItem,
  EligibilityList,
  EligibilityTitle,
  Footer,
  GiftIcon,
  HeaderTitle,
  Link,
  ModalContent,
  ModalHeader,
  SectionTitle,
  StyledModal,
  Timeline,
  TimelineContent,
  TimelineCopy,
  TimelineImage,
  TimelineImageWrapper,
  TimelineItem,
  TimelineNumber,
} from './styled-components';

const ELIGIBILITY_ITEMS = [
  'You complete a new patient exam and another dental service, such as a cleaning. Dental consultations, including x-rays, do not qualify for rewards.',
  'You are at least 18 years old on the date of your appointment.',
  'You have not claimed an Opencare reward in the past 2 years.',
  'You are not an existing patient of the dental practice.',
  'You are not using government funded insurance (e.g., Medicaid, Medicare, provincial health plans in Canada)',
  'Your appointment was booked using the name on your Opencare account. If one account was used to book appointments for two or more patients, only one patient will be able to claim a reward.',
];

const OpencareRewardsModal = ({
  opencareRewardsModalVisible,
  setOpencareRewardsModalVisible,
  isThirdPartyOffer,
  offerName,
}) => {
  const { layout } = useWindowDimensions();

  const steps = [
    {
      image: Timeline1,
      text: 'Book a new dentist with Opencare',
      show: true,
    },
    {
      image: Timeline2,
      text: 'Visit your new Opencare dentist',
      show: !isThirdPartyOffer,
    },
    {
      image: Timeline3,
      text: 'Submit your receipt to opencare.com',
      show: !isThirdPartyOffer,
    },
    {
      image: Timeline4,
      text: isThirdPartyOffer
        ? (offerName || 'Reward')
        : `You get a ${offerName || 'reward'} by email`,
      show: true,
    },
  ].filter((step) => step.show);

  return (
    <StyledModal
      centered
      footer={null}
      onCancel={(e) => {
        e.preventDefault();
        setOpencareRewardsModalVisible(false);
      }}
      visible={opencareRewardsModalVisible}
      width={layout === DIMENSIONS.MOBILE ? '100vw' : '840px'}
    >
      <ModalHeader>
        <GiftIcon />
        <HeaderTitle>
          {offerName ? `Your ${offerName} Offer Is Active` : 'Your Offer Is Active'}
        </HeaderTitle>
      </ModalHeader>

      <ModalContent data-testid="offer-modal">
        <ContentCard>
          <SectionTitle>How to claim your offer</SectionTitle>

          <Timeline>
            {steps.map((step, index) => (
              <TimelineItem key={step.text}>
                <TimelineImageWrapper>
                  <TimelineImage src={step.image} role="presentation" alt="" />
                </TimelineImageWrapper>
                <TimelineContent>
                  <TimelineNumber>{index + 1}</TimelineNumber>
                  <TimelineCopy>{step.text}</TimelineCopy>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </ContentCard>

        <EligibilityCard>
          <EligibilityTitle>Am I eligible?</EligibilityTitle>
          <EligibilityList>
            {ELIGIBILITY_ITEMS.map((text) => (
              <EligibilityItem key={text}>
                <Checkmark />
                <span>{text}</span>
              </EligibilityItem>
            ))}
          </EligibilityList>
        </EligibilityCard>

        <Footer>
          For more information, visit{' '}
          <Link href={OFFER_TERMS_URL} target="_blank" rel="noopener noreferrer">
            www.opencare.com/terms/offers
          </Link>{' '}
          or contact{' '}
          <Link href="mailto:support@opencare.com" target="_blank" rel="noopener noreferrer">
            support@opencare.com
          </Link>
        </Footer>
      </ModalContent>
    </StyledModal>
  );
};

OpencareRewardsModal.propTypes = {
  opencareRewardsModalVisible: PropTypes.bool,
  setOpencareRewardsModalVisible: PropTypes.func,
  offerName: PropTypes.string,
  isThirdPartyOffer: PropTypes.bool,
};

export default OpencareRewardsModal;
